import { useMemo } from "react";
import type { Node, Edge } from "@xyflow/react";

export interface TreeNodeData {
  id: string;
  label: string;
  code?: string;
  legalEntity?: string;
  parentId?: string | null;
  childCount: number;
  depth: number;
  [key: string]: unknown;
}

interface TreeNode {
  id: string;
  data: TreeNodeData;
  children: TreeNode[];
}

const NODE_WIDTH = 220;
const NODE_HEIGHT = 90;
const HORIZONTAL_GAP = 40;
const VERTICAL_GAP = 80;

/**
 * Build a tree structure from flat department data.
 * Handles multiple roots (departments with no parent).
 */
function buildTree(items: Record<string, unknown>[]): TreeNode[] {
  const nodeMap = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  // First pass: create all nodes
  for (const item of items) {
    const id = String(item.id || "");
    if (!id) continue;

    // Resolve display name: prefer localized name, fall back to keyedName/id
    const label = String(
      item.departmentNameVi || item.departmentNameEn || item.name || item.keyedName || id,
    );

    nodeMap.set(id, {
      id,
      data: {
        id,
        label,
        code: (item.itemNumber ?? item.departmentCode ?? "") as string,
        legalEntity: (item.entityName ?? item.legalEntityDisplay ?? "") as string,
        parentId: null,
        childCount: 0,
        depth: 0,
      },
      children: [],
    });
  }

  // Second pass: link parent-child
  for (const item of items) {
    const id = String(item.id || "");
    const node = nodeMap.get(id);
    if (!node) continue;

    // Try multiple possible field names for parent department reference
    const parentId =
      (item.parentDeptId as string) ||
      (item.parentDepartment as string) ||
      (item.parent_department as string) ||
      null;

    if (parentId && nodeMap.has(parentId)) {
      node.data.parentId = parentId;
      const parent = nodeMap.get(parentId)!;
      parent.children.push(node);
      parent.data.childCount++;
    } else {
      roots.push(node);
    }
  }

  // Assign depths
  function setDepth(node: TreeNode, depth: number) {
    node.data.depth = depth;
    for (const child of node.children) {
      setDepth(child, depth + 1);
    }
  }
  for (const root of roots) {
    setDepth(root, 0);
  }

  return roots;
}

/**
 * Calculate subtree width (total horizontal space needed).
 */
function getSubtreeWidth(node: TreeNode): number {
  if (node.children.length === 0) {
    return NODE_WIDTH;
  }
  const childrenWidth = node.children.reduce(
    (sum, child) => sum + getSubtreeWidth(child) + HORIZONTAL_GAP,
    -HORIZONTAL_GAP,
  );
  return Math.max(NODE_WIDTH, childrenWidth);
}

/**
 * Position nodes using a top-down tree layout.
 */
function positionNodes(
  roots: TreeNode[],
): { nodes: Node<TreeNodeData>[]; edges: Edge[] } {
  const nodes: Node<TreeNodeData>[] = [];
  const edges: Edge[] = [];

  // Calculate total width of all root trees
  let totalWidth = 0;
  const rootWidths: number[] = [];
  for (const root of roots) {
    const w = getSubtreeWidth(root);
    rootWidths.push(w);
    totalWidth += w;
  }
  totalWidth += (roots.length - 1) * HORIZONTAL_GAP * 2;

  // Position each root tree
  let startX = -totalWidth / 2;

  for (let i = 0; i < roots.length; i++) {
    const rootWidth = rootWidths[i];
    positionSubtree(roots[i], startX, 0, rootWidth, nodes, edges);
    startX += rootWidth + HORIZONTAL_GAP * 2;
  }

  return { nodes, edges };
}

function positionSubtree(
  node: TreeNode,
  left: number,
  top: number,
  availableWidth: number,
  nodes: Node<TreeNodeData>[],
  edges: Edge[],
) {
  // Center this node in available width
  const x = left + (availableWidth - NODE_WIDTH) / 2;
  const y = top;

  nodes.push({
    id: node.id,
    type: "departmentNode",
    position: { x, y },
    data: node.data,
  });

  if (node.children.length === 0) return;

  // Calculate children widths
  const childWidths = node.children.map((c) => getSubtreeWidth(c));
  const totalChildrenWidth =
    childWidths.reduce((s, w) => s + w, 0) +
    (node.children.length - 1) * HORIZONTAL_GAP;

  // Start children centered under parent
  let childLeft = left + (availableWidth - totalChildrenWidth) / 2;
  const childTop = top + NODE_HEIGHT + VERTICAL_GAP;

  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];
    const childWidth = childWidths[i];

    // Add edge from parent to child
    edges.push({
      id: `${node.id}-${child.id}`,
      source: node.id,
      target: child.id,
      type: "smoothstep",
    });

    positionSubtree(child, childLeft, childTop, childWidth, nodes, edges);
    childLeft += childWidth + HORIZONTAL_GAP;
  }
}

/**
 * Hook that converts flat department data into positioned nodes and edges
 * for React Flow rendering.
 */
export function useTreeLayout(data: Record<string, unknown>[]) {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return { nodes: [], edges: [] };
    }
    const roots = buildTree(data);
    return positionNodes(roots);
  }, [data]);
}
